import React, { ReactElement } from "react";
import { useGetTicketStatesQuery, User } from "../../../__generated__/api";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";

export interface TicketStatesPickerProps<TIsMulti = false, TIsClearable=false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    className?: string;
    isClearable?: TIsClearable;
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (
        sourceId: (TIsMulti extends true ? string[] : string) | (TIsClearable extends true ? undefined : never),
        text?: (TIsMulti extends true ? string[] : string)
    ) => void;
}

const TicketStates = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, className, isClearable,
}: TicketStatesPickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useGetTicketStatesQuery();

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<User>[] : Option<User>;
    type ValueType = TIsMulti extends true ? Option<User>[] : Option<User>;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values = data?.ticketStates?.map(item => ({
        label: item.name as string,
        value: item.id.toString() as string,
    })) ?? [];

    const handleSelected = (selected: SelectedType) => {
        if (!selected) {
            (onSelected as (empty: unknown) => void)(undefined);
            return;
        }
        const vals = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<User>).value) as Parameters<typeof onSelected>[0];

        const texts = (Array.isArray(selected)
            ? selected.map(row => values.find(val => val.label === row)?.label)
            : (selected as Option<User>).label) as Parameters<typeof onSelected>[0];
        onSelected(vals, texts);
    };

    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    return (
        <Select
            ref={ref}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={values ?? []}
            value={selected as ValueType}
            className={className}
            isClearable={isClearable}
        />
    );
});

TicketStates.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    isClearable: undefined,
    className: undefined,
};

export default TicketStates as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false
> (
    props: TicketStatesPickerProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof TicketStates>;
